import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import {
  IDoesFilterPassParams,
  IFilterParams,
  ISimpleFilterModel,
  RowNode
} from 'ag-grid-community'
import { BadgeOptionOverrides } from 'components/TailwindUIToolkit/badges'
import { CheckboxList } from './CheckboxList'

export interface SetFilterModel<T> extends ISimpleFilterModel {
  filter: T[]
  filterType: 'contains'
  options: T[]
}

interface SetFilterProps extends IFilterParams {
  // A server-paged grid uses this filter for its selection UI only.
  serverSide?: boolean
  badgeOptions?: BadgeOptionOverrides
  values?: FilterValue[] | (() => FilterValue[])
  formatValue?: (value: FilterValue) => string
}

type FilterValue = string | null | undefined

const normalizeFilterValue = (value: FilterValue): FilterValue =>
  typeof value === 'string' ? value.trim() : value

const filterValueLabel = (value: FilterValue) =>
  value === null || value === undefined || value === '' ? '(Blanks)' : value

// TODO: allow passing of generic type to be used for the filter options/items
export default forwardRef((props: SetFilterProps, ref) => {
  const { api, colDef, column, columnApi, context, filterChangedCallback } = props
  const getValue = props.valueGetter

  const valueGetter = useCallback(
    (node: RowNode<any>) =>
      normalizeFilterValue(
        getValue({
          api,
          colDef,
          column,
          columnApi,
          context,
          data: node.data,
          getValue: (field) => node.data[field],
          node
        })
      ),
    [api, colDef, column, columnApi, context, getValue]
  )

  const collectFilterOptions = useCallback(
    (selectedItems: FilterValue[] = []) => {
      const filterSet = new Set<FilterValue>()
      if (props.values !== undefined) {
        const values = typeof props.values === 'function' ? props.values() : props.values
        values.forEach((value) => filterSet.add(normalizeFilterValue(value)))
      } else {
        api.forEachLeafNode((node) => {
          filterSet.add(valueGetter(node))
        })
      }
      selectedItems.forEach((item) => {
        filterSet.add(item)
      })
      return Array.from(filterSet)
    },
    [api, valueGetter, props.values]
  )

  const [filterItems, setFilterItems] = useState<FilterValue[]>([])
  const [filterOptions, setFilterOptions] = useState<FilterValue[]>(() => collectFilterOptions())

  const refreshFilterOptions = useCallback(() => {
    setFilterOptions(collectFilterOptions(filterItems))
  }, [collectFilterOptions, filterItems])

  // expose AG Grid Filter Lifecycle callbacks
  useImperativeHandle(
    ref,
    () => {
      return {
        doesFilterPass(params: IDoesFilterPassParams) {
          if (props.serverSide) return true
          const { node } = params
          const value = valueGetter(node)
          if (filterItems.includes(value)) {
            return true
          }
          return false
        },

        isFilterActive() {
          return filterItems != null && filterItems.length > 0
        },

        getModel() {
          if (!this.isFilterActive()) {
            return null
          }

          return { filter: filterItems, options: filterOptions }
        },

        getModelAsString() {
          return filterItems.map(props.formatValue || filterValueLabel).join(', ')
        },

        setModel(model: SetFilterModel<FilterValue>) {
          setFilterItems(model == null ? [] : model.filter.map(normalizeFilterValue))
          if (model?.options !== undefined) {
            setFilterOptions(model.options.map(normalizeFilterValue))
          }
        },

        onNewRowsLoaded() {
          refreshFilterOptions()
        }
      }
    },
    [
      filterItems,
      filterOptions,
      refreshFilterOptions,
      valueGetter,
      props.formatValue,
      props.serverSide
    ]
  )

  useEffect(() => {
    filterChangedCallback()
  }, [filterChangedCallback, filterItems])

  useEffect(() => {
    refreshFilterOptions()
  }, [refreshFilterOptions])

  const handleOptionClicked = (optionValue: FilterValue, checked: boolean) => {
    setFilterItems((currentItems) => {
      if (checked) {
        return currentItems.includes(optionValue) ? currentItems : [...currentItems, optionValue]
      }
      return currentItems.filter((item) => item !== optionValue)
    })
  }
  return (
    <div className='ag-input-wrapper ag-set-filter'>
      <CheckboxList
        options={filterOptions?.map((option) => ({
          label: props.formatValue?.(option) ?? filterValueLabel(option),
          value: option,
          checked: !!filterItems.includes(option)
        }))}
        onOptionClicked={handleOptionClicked}
        badgeOptions={props.badgeOptions}
        sortByLabel={!!props.formatValue}
      />
    </div>
  )
})
